/**
 * Fixed-point decimal helpers backed by `bigint`. Financial values never pass through JS binary
 * floating point: they are parsed from / formatted to decimal strings exactly as PostgreSQL
 * `numeric` returns them.
 */
export class DecimalFormatError extends Error {
    name = 'DecimalFormatError';
}
/**
 * How a value that does not fit the target scale is rounded. There is deliberately no default:
 * the founders' rounding policy is an open decision (see docs/domain-model.md §5, decision 6),
 * so every caller must choose explicitly.
 */
export const ROUNDING_MODES = ['HALF_UP', 'HALF_EVEN', 'TOWARD_ZERO', 'AWAY_FROM_ZERO'];
const DECIMAL_PATTERN = /^(-)?(\d+)(?:\.(\d+))?$/;
/** Parses a decimal string into integer units at `scale`. Rejects values needing rounding. */
export function parseFixed(value, scale) {
    const match = DECIMAL_PATTERN.exec(value.trim());
    if (match === null) {
        throw new DecimalFormatError(`"${value}" is not a plain decimal number`);
    }
    const [, sign, whole = '0', fraction = ''] = match;
    const significantFraction = fraction.replace(/0+$/, '');
    if (significantFraction.length > scale) {
        throw new DecimalFormatError(`"${value}" has more than ${scale} decimal places`);
    }
    const units = BigInt(whole + fraction.padEnd(scale, '0').slice(0, scale));
    return sign === '-' ? -units : units;
}
/** Formats integer units at `scale` as a decimal string with exactly `scale` fraction digits. */
export function formatFixed(units, scale) {
    const negative = units < 0n;
    const digits = (negative ? -units : units).toString().padStart(scale + 1, '0');
    const whole = digits.slice(0, digits.length - scale);
    const fraction = digits.slice(digits.length - scale);
    const body = scale === 0 ? whole : `${whole}.${fraction}`;
    return negative ? `-${body}` : body;
}
/** Integer division `numerator / denominator` rounded with an explicit mode. */
export function divideRounded(numerator, denominator, mode) {
    if (denominator === 0n) {
        throw new RangeError('Division by zero');
    }
    const negative = numerator < 0n !== denominator < 0n;
    const n = numerator < 0n ? -numerator : numerator;
    const d = denominator < 0n ? -denominator : denominator;
    const quotient = n / d;
    const remainder = n % d;
    let magnitude = quotient;
    if (remainder !== 0n) {
        const twice = remainder * 2n;
        switch (mode) {
            case 'TOWARD_ZERO':
                break;
            case 'AWAY_FROM_ZERO':
                magnitude += 1n;
                break;
            case 'HALF_UP':
                if (twice >= d)
                    magnitude += 1n;
                break;
            case 'HALF_EVEN':
                if (twice > d || (twice === d && quotient % 2n === 1n))
                    magnitude += 1n;
                break;
        }
    }
    return negative ? -magnitude : magnitude;
}
//# sourceMappingURL=fixed-decimal.js.map